import { useEffect, useRef, useState } from 'react';
import { Brain, CheckSquare, ChevronDown, PenLine, Shuffle, BookOpen, Tag } from 'lucide-react';
import api from '../../services/api';
import Illustration from './Illustration';
import illuQuiz from '../../assets/illustrations/choisir-un-sujet.svg?raw';
import illuExamen from '../../assets/illustrations/me-tester-sur-tout.svg?raw';
import illuEntrainement from '../../assets/illustrations/travailler-mes-points-faibles.svg?raw';
import illuNotions from '../../assets/illustrations/approfondir-une-notion.svg?raw';
import illuRedaction from '../../assets/illustrations/quiz-libre.svg?raw';

// Dessins des cartes (docs/img/illustrations-choix-et-notions-svg), aux couleurs de la thématique ; réduits sur mobile (ds-arcnav.css).
const ILLUSTRATIONS = { quiz: illuQuiz, examen: illuExamen, entrainement: illuEntrainement, redaction: illuRedaction, notions: illuNotions };

const MODES = [
    {
        id: 'quiz',
        action: 'Choisir un sujet',
        short: 'Un sujet',
        mode: 'Mode Quiz',
        icon: CheckSquare,
        desc: 'Choisis précisément le sujet que tu veux travailler, puis lance une séance classique ou en apprentissage.',
        linkLabel: 'Voir les sujets',
    },
    {
        id: 'examen',
        action: 'Me tester sur tout',
        short: 'Mélange',
        mode: 'Mode Mélange',
        icon: Shuffle,
        desc: 'Compose un quiz transversal avec plusieurs sujets, puis règle sa longueur et sa difficulté.',
        linkLabel: 'Configurer le mélange',
    },
    {
        id: 'entrainement',
        // « Révisions » appartient à la ligne « Conseillé pour toi » (séance SM2 du jour) ;
        // la carte ouvre la boîte à outils ciblée, choisie par le candidat lui-même.
        action: 'Travailler mes points faibles',
        short: 'Points faibles',
        mode: 'Mode Entraînement',
        icon: Brain,
        desc: 'Choisis toi-même : erreurs à refaire, notions fragiles, quiz intelligent et carte de maîtrise.',
        linkLabel: 'Voir les entraînements',
    },
    {
        // Réponses rédigées (QRC) : mode à part entière depuis le 29/09/2026, affiché seulement
        // quand la thématique en propose (voir le filtre dans le composant).
        id: 'redaction',
        action: 'Répondre à des questions courtes',
        short: 'Rédaction',
        mode: 'Mode Rédaction',
        icon: PenLine,
        desc: 'Rédige ta réponse avec tes mots : la correction te dit point par point ce qui est acquis, ce qui manque et ce qui est contredit.',
        linkLabel: 'Voir les questions',
    },
    {
        // Notions à la carte (migration 183) : affiché seulement si la thématique en propose à cette personne.
        id: 'notions',
        action: 'Approfondir une notion',
        short: 'Notions',
        mode: 'Fiche + 20 questions',
        icon: BookOpen,
        desc: "Une fiche pour comprendre l'essentiel d'un sujet, puis 20 questions pour vérifier ce que tu as retenu. À l'unité ou en pack, en plus de la thématique.",
        linkLabel: 'Voir les notions',
    },
];

function RedactionVisual() {
    return (
        <span className="mode-dock__visual mode-dock__visual--redaction" aria-hidden="true">
            <i /><i /><i className="is-short" />
        </span>
    );
}

function QuizVisual() {
    return (
        <span className="mode-dock__visual mode-dock__visual--quiz" aria-hidden="true">
            <i />
            <i className="is-selected"><b /></i>
            <i />
        </span>
    );
}

function MixVisual({ questionCount }) {
    return (
        <span className="mode-dock__visual mode-dock__visual--mix" aria-hidden="true">
            <span className="mode-dock__mix-dots"><i /><i /><i /><i /></span>
            <b className="mode-dock__mix-arrow">→</b>
            <strong>{questionCount} Q</strong>
        </span>
    );
}

function ProgressVisual() {
    return (
        <span className="mode-dock__visual mode-dock__visual--progress" aria-hidden="true">
            <i><b style={{ '--dock-bar': '70%' }} /></i>
            <i className="is-priority"><b style={{ '--dock-bar': '38%' }} /></i>
            <i><b style={{ '--dock-bar': '54%' }} /></i>
        </span>
    );
}

/**
 * Le nom ArcNav est conservé pour éviter de casser les imports et les ancres
 * existantes. Visuellement, le composant est désormais un dock décisionnel.
 */
export default function ArcNav({
    activeTab,
    onTabChange,
    scopeColor,
    scopeId = 0,
    categories = [],
    examenNbQuestions = 20,
    hasUser = false,
    // Recommandation du parcours guidé en tête : elle remplace le badge « Recommandé » des cartes.
    nextStep = null,
    // Reprise après absence : en-tête « Bon retour » ({ eyebrow, title, sub, dismissLabel, onDismiss })
    // et raccourcis affichés sous l'en-tête quand il n'y a pas de recommandation guidée.
    greeting = null,
    extra = null,
    // Nombre de notions proposées dans cette thématique (0 : la tuile « Notions » n'apparaît pas).
    notionsCount = 0,
    // Notions ouvertes au grand public (NOTIONS_OPEN). La tuile Notions porte « À la carte » (achat à part), Rédaction garde « Nouveau ».
    notionsNew = false,
}) {
    const rootRef = useRef(null);
    const [dueSummary, setDueSummary] = useState(null);
    const [dueLoading, setDueLoading] = useState(false);
    const accentColor = scopeColor || '#6366f1';

    const subjectCount = categories.reduce((total, category) => (
        total + (category.sous_sujets || []).filter(subject => subject.status !== 'OBSOLETE').length
    ), 0);
    // Réponses rédigées : la carte n'existe que si la thématique en propose
    const qrcSubjects = categories.flatMap(category => (category.sous_sujets || []).filter(s => Number(s.qrc_count || 0) > 0 && s.status !== 'OBSOLETE'));
    const qrcCount = qrcSubjects.reduce((total, s) => total + Number(s.qrc_count || 0), 0);
    const modes = MODES.filter(mode => (mode.id !== 'redaction' || qrcCount > 0) && (mode.id !== 'notions' || notionsCount > 0));
    const activeMode = modes.find(mode => mode.id === activeTab) || modes[0];
    const answeredCount = categories.reduce((total, category) => (
        total + (category.sous_sujets || []).reduce((subjectTotal, subject) => (
            subjectTotal + Number(subject.answered_count || 0)
        ), 0)
    ), 0);

    useEffect(() => {
        let cancelled = false;
        if (!hasUser || !scopeId) {
            setDueSummary(null);
            setDueLoading(false);
            return undefined;
        }

        setDueLoading(true);
        api.get('/qcm/sm2-due.php', { params: { scope_id: scopeId, summary: 1 } })
            .then(({ data }) => {
                if (!cancelled) setDueSummary(data?.data || null);
            })
            .catch(() => {
                if (!cancelled) setDueSummary(null);
            })
            .finally(() => {
                if (!cancelled) setDueLoading(false);
            });

        return () => { cancelled = true; };
    }, [hasUser, scopeId]);

    const dueNow = Number(dueSummary?.due_today || 0) + Number(dueSummary?.due_overdue || 0);
    // Avec la ligne conseillée, le nombre de révisions dues n'est dit qu'une fois (dans la ligne).
    const progressMetric = dueLoading && !nextStep
        ? 'Analyse de tes révisions…'
        : dueNow > 0 && !nextStep
            ? `${dueNow} révision${dueNow > 1 ? 's' : ''} due${dueNow > 1 ? 's' : ''} aujourd’hui`
            : answeredCount > 0
                ? `Progression basée sur ${answeredCount.toLocaleString('fr-FR')} réponses`
                : 'Commence pour personnaliser tes séances';

    const metrics = {
        quiz: `${subjectCount} sujet${subjectCount > 1 ? 's' : ''} disponible${subjectCount > 1 ? 's' : ''}`,
        examen: `Quiz conseillé : ${examenNbQuestions} questions`,
        entrainement: progressMetric,
        redaction: `${qrcCount} question${qrcCount > 1 ? 's' : ''} rédigée${qrcCount > 1 ? 's' : ''} · ${qrcSubjects.length} sujet${qrcSubjects.length > 1 ? 's' : ''}`,
        notions: `${notionsCount} notion${notionsCount > 1 ? 's' : ''} à approfondir`,
    };

    const scrollToContent = () => {
        const content = rootRef.current?.parentElement?.querySelector('.sl-content, .mt-inline__content');
        content?.scrollIntoView({
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    return (
        <section
            ref={rootRef}
            className="arc-nav mode-dock"
            style={{ '--mode-accent': accentColor, '--mode-accent-base': accentColor }}
            aria-labelledby="mode-dock-title"
        >
            <div className={`mode-dock__heading ${greeting ? 'is-welcome' : ''}`}>
                <span>{greeting ? greeting.eyebrow : 'Ta prochaine étape'}</span>
                <h2 id="mode-dock-title">{greeting ? greeting.title : <>Que veux-tu faire maintenant&nbsp;?</>}</h2>
                {greeting?.sub && <p className="mode-dock__heading-sub">{greeting.sub}</p>}
                {greeting?.onDismiss && (
                    <button type="button" className="mode-dock__heading-dismiss" onClick={greeting.onDismiss}>
                        {greeting.dismissLabel || 'Masquer'}
                    </button>
                )}
            </div>

            {nextStep}
            {!nextStep && extra}

            <div className={`mode-dock__cards ${modes.length > 4 ? 'has-5' : modes.length > 3 ? 'has-4' : ''}`} role="group" aria-label="Choisir un mode de travail">
                {modes.map(mode => {
                    const Icon = mode.icon;
                    const isActive = activeMode.id === mode.id;
                    const illu = ILLUSTRATIONS[mode.id];
                    return (
                        <button
                            key={mode.id}
                            type="button"
                            aria-pressed={isActive}
                            className={`mode-dock__card ${isActive ? 'is-active' : ''}${illu ? ' has-illu' : ''}`}
                            onClick={() => {
                                onTabChange(mode.id);
                                // Mobile : la carte de description est masquée, on descend directement au contenu du mode.
                                if (window.matchMedia?.('(max-width: 640px)').matches) setTimeout(scrollToContent, 80);
                            }}
                        >
                            <span className="mode-dock__card-head">
                                {illu && <Illustration className="mode-dock__illu" svg={illu} color={accentColor} />}
                                <span className="mode-dock__icon"><Icon size={20} strokeWidth={2.2} /></span>
                                {mode.id === 'entrainement' && dueNow > 0 && !nextStep && (
                                    <span className="mode-dock__recommended"><i /> Recommandé</span>
                                )}
                                {mode.id === 'redaction' && <span className="mode-dock__nouveau">Nouveau</span>}
                                {/* Notions : contenus en plus, achetés à part (pas compris dans l'accès à la thématique). */}
                                {mode.id === 'notions' && <span className="mode-dock__nouveau mode-dock__extra" title="Fiches et questions en plus, à acheter à part"><Tag size={10} strokeWidth={2.6} /> À la carte</span>}
                            </span>
                            <strong className="mode-dock__action">{mode.action}</strong>
                            <strong className="mode-dock__short">{mode.short}</strong>
                            <span className="mode-dock__mode">{mode.mode}</span>
                            {mode.id === 'quiz' && <QuizVisual />}
                            {mode.id === 'examen' && <MixVisual questionCount={examenNbQuestions} />}
                            {mode.id === 'entrainement' && <ProgressVisual />}
                            {(mode.id === 'redaction' || mode.id === 'notions') && <RedactionVisual />}
                            <span className="mode-dock__metric">{metrics[mode.id]}</span>
                            <ChevronDown className="mode-dock__caret" size={19} strokeWidth={2.5} />
                        </button>
                    );
                })}
            </div>

            <div className="mode-dock__summary" aria-live="polite">
                <span>
                    <strong>{activeMode.action}</strong>
                    {activeMode.desc}
                </span>
                <button type="button" onClick={scrollToContent}>
                    {activeMode.linkLabel} <ChevronDown size={16} />
                </button>
            </div>
        </section>
    );
}
